import { Navigate, Outlet } from 'react-router';
import { BulkProvisioningJobsDropTable } from './Inventory/BulkProvisioningJobsDropTable.tsx';
import { SuspenseLoadingView } from 'common/components/SuspenseLoadingView.tsx';
import { TableSkeleton } from 'common/components/Table/TableSkeleton.tsx';
import { BulkProvisioningUploadButton } from './Components/BulkProvisioningUploadButton.tsx';
import { useState } from 'react';
import { FiltersModal } from './Filters/ProvisioningFiltersModal.tsx';
import { bulkProvisioningFileNameFilter } from './BulkProvisioningDataTestIds.ts';
import { BulkFiltersChips } from './Filters/ProvisioningFiltersChips.tsx';
import { useAbility } from 'permissions/hooks/useAbility.ts';
import { Actions, Subjects } from 'permissions/ability.ts';
import { SimInventoryCan } from 'permissions/PermissionProvider.tsx';
import { RefetchQueryButton } from 'common/components/Queries/RefetchQueryButton.tsx';
import MenuItem from '@mui/material/MenuItem';
import { Select } from 'common/components/Inputs/Select/Select.tsx';
import { useTranslation } from 'react-i18next';
import { AutoHideSnackbar } from 'common/Snackbar/AutoHideSnackbar.tsx';
import { AlertTitle } from 'common/Snackbar/AlertTitle.tsx';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import {
  downloadBulkTemplate,
  isTemplateType,
  TEMPLATES,
  TemplateType,
} from './BulkProvisioningApi/bulkProvisioningApi.ts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AuthError } from 'auth/utils.ts';
import { TextField } from 'common/components/Inputs/TextField/TextField.tsx';
import { provisioningQueries } from './utils/queries.ts';
import { useAtom, useAtomValue } from 'jotai';
import {
  GoogleTagManagerActions,
  sendGoogleTagManagerAction,
} from 'common/utils/sendGoogleTagManagerAction.ts';
import { SummaryReportButton } from './Components/SummaryReportButton.tsx';
import {
  TableLayout,
  TableLayoutActions,
  TableLayoutChips,
  TableLayoutFilters,
  TableLayoutSearchInput,
  TableLayoutTable,
} from 'common/components/Layouts/TableLayout/TableLayout.tsx';
import { useHasActiveProvisioningFilters } from './Filters/hooks/useHasActiveProvisioningFilters.ts';
import {
  bulkProvisioningFiltersAtom,
  fileNameFilterAtom,
} from 'atoms/filters/bulk-provisioning.ts';
import Portal from '@mui/material/Portal';
import { useHasFeatureFlag } from 'featureFlags/useHasFeatureFlag.ts';

type ExportError = {
  title: string;
  description: string;
};

export const BulkProvisioningJobs = () => {
  const productManagementEnabled = useHasFeatureFlag('ProductManagement');
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const filters = useAtomValue(bulkProvisioningFiltersAtom);
  const [fileName, setFileName] = useAtom(fileNameFilterAtom);

  const ability = useAbility();

  const [exportError, setExportError] = useState<ExportError | null>(null);
  const [downloadingSnackbarOpened, setDownloadingSnackbarOpened] = useState(false);

  const { mutateAsync: downloadTemplate, isPending: isDownloading } = useMutation({
    mutationKey: provisioningQueries.downloadTemplate,
    mutationFn: (selectedTemplate: TemplateType) => {
      return downloadBulkTemplate(selectedTemplate);
    },
    onSuccess: (_data, variables) => {
      const googleTagManagerActionName = `Download${variables}`;
      sendGoogleTagManagerAction(
        GoogleTagManagerActions[googleTagManagerActionName as keyof typeof GoogleTagManagerActions],
      );
      queryClient.invalidateQueries({ queryKey: provisioningQueries.downloadTemplate });
    },
    onError: (error) => {
      if (error instanceof AuthError) {
        throw error;
      } else {
        setExportError({
          title: t('common.requestFailed'),
          description: t('common.somethingWrong'),
        });
      }
    },
  });

  const getTemplateName = (template: TemplateType) => {
    if (template === 'MasterTemplate') {
      return t('bulkProvisioning.masterTemplate');
    }
    if (template === 'BulkProvisioning') {
      return t('bulkProvisioning.bulkProvisioning');
    }
    if (template === 'UnitDataAdjustment') {
      return t('bulkProvisioning.unitDataAdjustment');
    }
    if (template === 'SimProfileManagement') {
      return t('bulkProvisioning.simProfileManagement');
    }
  };

  if (isDownloading && !downloadingSnackbarOpened) {
    setDownloadingSnackbarOpened(true);
  }

  const hasChips = useHasActiveProvisioningFilters(filters);

  return ability.can(Actions.read, Subjects.bulkProvisioning) ? (
    <>
      <TableLayout>
        <TableLayoutFilters>
          <TableLayoutSearchInput>
            <TextField
              data-testid={bulkProvisioningFileNameFilter}
              value={fileName}
              onChange={(event) => setFileName(event.target.value)}
              label={t('common.fileName')}
              showSearchIcon={true}
            />
          </TableLayoutSearchInput>
          <FiltersModal />
        </TableLayoutFilters>

        <TableLayoutChips hide={!hasChips}>
          <BulkFiltersChips />
        </TableLayoutChips>
        <TableLayoutActions>
          <RefetchQueryButton queryKey={provisioningQueries.all} />
          <SummaryReportButton />
          <Select
            name={t('bulkProvisioning.downloadTemplate')}
            displayEmpty
            renderValue={() => t('bulkProvisioning.downloadTemplate')}
            fullWidth={false}
            data-testid="downloadTemplate"
            variant="filled"
            onChange={(event) => {
              if (!isDownloading && event.target.value && isTemplateType(event.target.value)) {
                downloadTemplate(event.target.value);
              }
            }}
            defaultValue={null}
            value={null}
            disabled={isDownloading}
          >
            {productManagementEnabled ? (
              <MenuItem
                key={TEMPLATES.SIM_PROFILE_MANAGEMENT}
                value={TEMPLATES.SIM_PROFILE_MANAGEMENT}
                disabled={isDownloading}
              >
                {getTemplateName(TEMPLATES.SIM_PROFILE_MANAGEMENT)}
              </MenuItem>
            ) : null}
            {productManagementEnabled ? null : (
              <MenuItem key={TEMPLATES.MASTER} value={TEMPLATES.MASTER} disabled={isDownloading}>
                {getTemplateName(TEMPLATES.MASTER)}
              </MenuItem>
            )}
            {productManagementEnabled ? null : (
              <MenuItem
                key={TEMPLATES.BULK_PROVISIONING}
                value={TEMPLATES.BULK_PROVISIONING}
                disabled={isDownloading}
              >
                {getTemplateName(TEMPLATES.BULK_PROVISIONING)}
              </MenuItem>
            )}
            <MenuItem
              key={TEMPLATES.UNIT_DATA_ADJUSTMENT}
              value={TEMPLATES.UNIT_DATA_ADJUSTMENT}
              disabled={isDownloading}
            >
              {getTemplateName(TEMPLATES.UNIT_DATA_ADJUSTMENT)}
            </MenuItem>
          </Select>
          <SimInventoryCan I={Actions.upload} a={Subjects.bulkProvisioning}>
            <BulkProvisioningUploadButton />
          </SimInventoryCan>
        </TableLayoutActions>
        <TableLayoutTable>
          <SuspenseLoadingView
            loadFallback={<TableSkeleton showEndSkeleton />}
            queryKey={provisioningQueries.all}
          >
            <BulkProvisioningJobsDropTable />
            <Outlet />
          </SuspenseLoadingView>
        </TableLayoutTable>
      </TableLayout>
      <Portal>
        <Stack>
          <AutoHideSnackbar
            severity="success"
            open={downloadingSnackbarOpened}
            onClose={() => {
              setDownloadingSnackbarOpened(false);
            }}
          >
            <AlertTitle> {t('common.downloading')}!</AlertTitle>
          </AutoHideSnackbar>
          <AutoHideSnackbar
            open={!!exportError}
            severity="error"
            onClose={() => setExportError(null)}
          >
            <AlertTitle>{exportError?.title || ''}!</AlertTitle>
            <Typography variant="text1" color="text.white">
              {exportError?.description}
            </Typography>
          </AutoHideSnackbar>
        </Stack>
        ,
      </Portal>
    </>
  ) : (
    <Navigate replace to="/" />
  );
};
